/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 11/04/22
 */

import { AJV } from '@/utils/typeHelpers';
import {
    composeWithUi,
    ControlElement,
    createAjv,
    isControl,
    isLayout,
    Resolve,
    Runtime,
    UISchemaElement,
} from '@jsonforms/core';
import { has, set } from 'lodash';
import { DataReplacementElement } from '@/forms-renderers/chakra-ui/additional/DataReplacement/ChakraDataReplacementRenderer';

type Data = Record<string, unknown>;

const isDataReplacementElement = (uischema: UISchemaElement): uischema is DataReplacementElement => {
    const element = uischema as DataReplacementElement;
    return element.scopes !== undefined && Array.isArray(element.scopes);
};

/**
 * Get flat list of visible UI elements from a root UI element
 * - If a single control, returns an array with itself as the single element
 * - If a layout, returns flat list of child elements
 * @param data Data for controlling visibility
 * @param uischema Root UI element to process
 * @param ajv AJV instance for schema processing
 */
const getVisibleElements = (
    data: Data,
    uischema: UISchemaElement,
    ajv: AJV,
): (ControlElement | DataReplacementElement)[] => {
    if (Runtime.isVisible(uischema, data, ajv) && Runtime.isEnabled(uischema, data, ajv)) {
        if (isControl(uischema)) {
            return [uischema];
        } else if (isDataReplacementElement(uischema)) {
            return [uischema];
        } else if (isLayout(uischema)) {
            return uischema.elements.flatMap((element) => getVisibleElements(data, element, ajv));
        }
    }
    return [];
};

/**
 * Remove data that is not used by a visible UI element
 * @param data Data to strip and for controlling visibility
 * @param uischema UI schema
 * @todo Does not strip data from within ListWithDetail elements
 */
const stripHiddenData = (data: Data, uischema: UISchemaElement): Data => {
    const visibleElements = getVisibleElements(
        data,
        uischema,
        createAjv({
            useDefaults: true,
            allErrors: true,
        }),
    );
    const newData: typeof data = {};

    const retainData = (scope: string) => {
        const path = composeWithUi({ scope }, '');
        const propertyData = Resolve.data(data, path);
        if (has(data, path)) {
            set(newData, path, propertyData);
        }
    };

    for (const visibleElement of visibleElements) {
        if ('scopes' in visibleElement && visibleElement.scopes) {
            for (const scope of visibleElement.scopes) {
                if (scope) {
                    retainData(scope);
                }
            }
        }
        if ('scope' in visibleElement) {
            retainData(visibleElement.scope);
        }
    }
    return newData;
};

export default stripHiddenData;
